import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ModalController } from '@ionic/angular';
import { Subscription } from 'rxjs';
import { Company } from '../../api/models';
import { StateService } from '../../services/state.service';

interface GermanState {
  code: string;
  name: string;
}

@Component({
  selector: 'lib-company-form',
  templateUrl: './company-form.component.html',
  styleUrls: ['./company-form.component.scss'],
  standalone: false,
})
export class CompanyFormComponent implements OnInit, OnDestroy {
  private subs: Subscription[] = [];

  public companyId: string;
  public company: Company | null = null;

  public form: FormGroup;
  public submitted = false;
  public saving = false;

  public readonly germanStates: GermanState[] = [
    { code: 'bw', name: 'Baden-Württemberg' },
    { code: 'by', name: 'Bayern' },
    { code: 'be', name: 'Berlin' },
    { code: 'bb', name: 'Brandenburg' },
    { code: 'hb', name: 'Bremen' },
    { code: 'hh', name: 'Hamburg' },
    { code: 'he', name: 'Hessen' },
    { code: 'mv', name: 'Mecklenburg-Vorpommern' },
    { code: 'ni', name: 'Niedersachsen' },
    { code: 'nw', name: 'Nordrhein-Westfalen' },
    { code: 'rp', name: 'Rheinland-Pfalz' },
    { code: 'sl', name: 'Saarland' },
    { code: 'sn', name: 'Sachsen' },
    { code: 'st', name: 'Sachsen-Anhalt' },
    { code: 'sh', name: 'Schleswig-Holstein' },
    { code: 'th', name: 'Thüringen' },
  ];

  constructor(
    private fb: FormBuilder,
    public state: StateService,
    private modalController: ModalController
  ) {
    this.form = this.fb.group({
      name: ['', Validators.required],
      companyNumber: [''],

      address: ['', Validators.required],
      postalCode: ['', Validators.required],
      city: ['', Validators.required],
      state: ['bw', Validators.required],

      mail: ['', Validators.email],
      phone: [''],

      basicAccounting: [''],
      badWeatherAccounting: [''],
      publicHolidayAccounting: [''],
      sicknessAccounting: [''],
      vacationAccounting: [''],

      defaultConSiteFolders: [
        'Allgemein, Bilder, Lieferscheine, Dokumente, Pläne, Rapporte',
      ],
    });
  }

  ngOnInit(): void {
    if (this.companyId) {
      this.loadCompany();
    }
  }

  ngOnDestroy(): void {
    this.subs.forEach((sub) => sub.unsubscribe());
  }

  /**
   * Lädt die bestehende Company aus dem State.
   */
  private loadCompany(): void {
    this.subs.push(
      this.state.companies$.subscribe((companies) => {
        const company = companies.find((x) => x.id === this.companyId);

        if (!company) {
          return;
        }

        this.company = company;
        this.patchForm(company);
      })
    );
  }

  /**
   * Übernimmt die Daten der Company in das Formular.
   */
  private patchForm(company: Company): void {
    this.form.patchValue({
      name: company.name ?? '',
      companyNumber: company.companyNumber ?? '',

      address: company.address ?? '',
      postalCode: company.postalCode ?? '',
      city: company.city ?? '',
      state: company.state ?? 'bw',

      mail: company.mail ?? '',
      phone: company.phone ?? '',

      basicAccounting: company.basicAccounting ?? '',
      badWeatherAccounting: company.badWeatherAccounting ?? '',
      publicHolidayAccounting: company.publicHolidayAccounting ?? '',
      sicknessAccounting: company.sicknessAccounting ?? '',
      vacationAccounting: company.vacationAccounting ?? '',

      defaultConSiteFolders: company.defaultConSiteFolders?.join(', ') ?? '',
    });
  }

  /**
   * Gibt zurück, ob ein Formularfeld einen Validierungsfehler hat.
   */
  public hasError(controlName: string): boolean {
    const control = this.form.get(controlName);

    return !!(
      control &&
      control.invalid &&
      (control.touched || this.submitted)
    );
  }

  /**
   * Speichert die Company.
   */
  public save(): void {
    this.submitted = true;

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.saving = true;

    const value = this.form.getRawValue();

    const company: Company = {
      ...(this.company ?? {}),

      id: this.companyId ?? null,

      name: this.normalizeValue(value.name),
      companyNumber: this.normalizeValue(value.companyNumber),

      address: this.normalizeValue(value.address),
      postalCode: this.normalizeValue(value.postalCode),
      city: this.normalizeValue(value.city),
      state: value.state,

      mail: this.normalizeValue(value.mail),
      phone: this.normalizeValue(value.phone),

      basicAccounting: this.normalizeValue(value.basicAccounting),
      badWeatherAccounting: this.normalizeValue(value.badWeatherAccounting),
      publicHolidayAccounting: this.normalizeValue(
        value.publicHolidayAccounting
      ),
      sicknessAccounting: this.normalizeValue(value.sicknessAccounting),
      vacationAccounting: this.normalizeValue(value.vacationAccounting),

      defaultConSiteFolders: this.parseFolders(value.defaultConSiteFolders),
    };

    try {
      if (this.companyId) {
        //this.state. updateCompany(company);
      } else {
        this.state.createCompany(company);
      }

      this.modalController.dismiss(company, 'saved');
    } catch (error) {
      console.error('Fehler beim Speichern der Company', error);
      this.saving = false;
    }
  }

  /**
   * Schließt das Modal ohne zu speichern.
   */
  public cancel(): void {
    this.modalController.dismiss(null, 'cancel');
  }

  /**
   * Wandelt leere Strings in null um.
   */
  private normalizeValue(value: string | null | undefined): string | null {
    if (value === null || value === undefined) {
      return null;
    }

    const normalized = value.trim();

    return normalized.length > 0 ? normalized : null;
  }

  /**
   * Erlaubt die Eingabe der ConSite-Ordner als kommaseparierte Liste.
   */
  private parseFolders(value: string | null | undefined): string[] | null {
    if (!value || !value.trim()) {
      return null;
    }

    const folders = value
      .split(',')
      .map((folder) => folder.trim())
      .filter((folder) => folder.length > 0);

    return folders.length > 0 ? folders : null;
  }

  /**
   * Gibt den Namen eines Bundeslandes anhand seines Kürzels zurück.
   */
  public getStateName(code: string | null | undefined): string {
    return this.germanStates.find((x) => x.code === code)?.name ?? '';
  }
}
